/**
 * Built blocks: where a world has buildings, as a coarse grid (docs/features/play-world-osm.md, buildings). Not the
 * buildings themselves: each cell of about 30 m is built or not, with one height class, so a town reads as a town
 * and a city centre stands above its suburbs, drawn as abstract blocks.
 *
 * - **Cells.** Two cells of the terrain-type grid a side (29 m on Cape Cobra's 30 km world, more on larger worlds),
 *   aligned with it.
 * - **Built.** OpenStreetMap's footprints are rasterised at four samples a cell side as each tile is read (only the
 *   samples are kept, never the outlines, which run to millions of vertices over a city); a cell is built when they
 *   cover a fifth of it. Where OpenStreetMap maps no building near a cell WorldCover calls built-up (a town it hasn't mapped),
 *   the cell is built too, unless a road crosses it, so its streets stay open.
 * - **Height.** The tallest building the map gives a height for (`height`, or `building:levels` × 3 + 2), else the
 *   cell's density: classes 1 (houses) to 5 (high-rise).
 *
 * In a world pack the grid is sparse: row by row, runs of built cells (a gap, a length, then a class byte each). No
 * dependencies, so the viewer's pack reader stays small.
 */

import type { GridSpec } from './grid'

export interface BuildingGrid extends GridSpec {
  /** Height class per cell, row-major from the north-west corner: 0 none, 1 low … 5 high-rise. */
  values: Uint8Array
}

/** A building footprint to rasterise: rings and the tile's own cells, in cells of the terrain-type grid. */
export interface FootprintPiece {
  rings: number[][]
  clip: [number, number, number, number]
  /** Metres, 0 when the map doesn't say. */
  height: number
}

/** Terrain-type cells per block cell, a side. */
export const TYPE_CELLS_PER_BLOCK = 2
/** Footprint samples per block cell, a side. */
const SAMPLES = 4
/** A cell is built when footprints cover this much of it. */
export const BUILT_COVERAGE = 0.2
/** Footprints this dense make a block of the second class when no height is mapped (terraces, flats). */
export const DENSE_COVERAGE = 0.6
/** WorldCover's built-up counts only this many cells from the nearest mapped building. */
const MAPPED_REACH_CELLS = 2

/** The tallest class each height (metres) reaches up to. */
export const CLASS_HEIGHTS_M: readonly number[] = [0, 10, 22, 50, 90, Infinity]

export function heightClass(heightM: number): number {
  if (!(heightM > 0)) return 0
  return CLASS_HEIGHTS_M.findIndex((top) => heightM < top)
}

export function blockGridSpec(types: GridSpec): GridSpec {
  const cols = Math.max(1, Math.round(types.cols / TYPE_CELLS_PER_BLOCK))
  const rows = Math.max(1, Math.round(types.rows / TYPE_CELLS_PER_BLOCK))
  return { bounds: types.bounds, cols, rows, cellM: types.widthM / cols, widthM: types.widthM, heightM: types.heightM }
}

/** Even–odd fill by sample centre, keeping the larger of `value` and what is there; rings and clip in sample units. */
function fillSamples(samples: Uint8Array, cols: number, rings: number[][], clip: readonly number[], value: number): void {
  const [c0, c1, r0, r1] = clip
  const crossings: number[] = []
  for (let r = r0; r < r1; r++) {
    const y = r + 0.5
    crossings.length = 0
    for (const ring of rings) {
      const count = ring.length / 2
      for (let i = 0, j = count - 1; i < count; j = i++) {
        const yi = ring[i * 2 + 1]
        const yj = ring[j * 2 + 1]
        if (yi > y !== yj > y) crossings.push(ring[i * 2] + ((y - yi) * (ring[j * 2] - ring[i * 2])) / (yj - yi))
      }
    }
    if (crossings.length < 2) continue
    crossings.sort((a, b) => a - b)
    for (let k = 0; k + 1 < crossings.length; k += 2) {
      const from = Math.max(c0, Math.ceil(crossings[k] - 0.5))
      const to = Math.min(c1, Math.ceil(crossings[k + 1] - 0.5))
      for (let c = from; c < to; c++) if (value > samples[r * cols + c]) samples[r * cols + c] = value
    }
  }
}

/**
 * Footprints rasterised onto a world's block grid, SAMPLES a cell side: 0 open, else the building's height in metres
 * plus one (1 when the map gives none), the tallest where they overlap, so the order they come in doesn't change it.
 */
export interface FootprintSamples {
  cols: number
  rows: number
  values: Uint8Array
}

/** Empty samples for the blocks over a terrain-type grid. */
export function footprintSamples(types: GridSpec): FootprintSamples {
  const { cols, rows } = blockGridSpec(types)
  return { cols: cols * SAMPLES, rows: rows * SAMPLES, values: new Uint8Array(cols * SAMPLES * rows * SAMPLES) }
}

/** Rasterises one footprint into the samples. */
export function addFootprint(samples: FootprintSamples, piece: FootprintPiece): void {
  const scale = SAMPLES / TYPE_CELLS_PER_BLOCK
  const rings = piece.rings.map((ring) => ring.map((value) => value * scale))
  const [c0, c1, r0, r1] = piece.clip.map((value) => Math.round(value * scale))
  const clip = [Math.max(0, c0), Math.min(samples.cols, c1), Math.max(0, r0), Math.min(samples.rows, r1)]
  fillSamples(samples.values, samples.cols, rings, clip, Math.min(255, Math.round(piece.height) + 1))
}

export interface BuildingGridInput {
  /** The terrain-type grid the blocks align with, and its codes (to find WorldCover's built-up). */
  types: GridSpec & { values: Uint8Array }
  builtCode: number
  /** The world's footprints, rasterised as they were read (`footprintSamples`, `addFootprint`). */
  footprints: FootprintSamples
  /** Roads, as [x, y, …] in the area's frame (metres east and north of its centre). */
  roads: readonly ArrayLike<number>[]
}

export function buildingGrid({ types, builtCode, footprints, roads }: BuildingGridInput): BuildingGrid {
  const spec = blockGridSpec(types)
  const { cols, rows } = spec
  const { cols: sampleCols, values: samples } = footprints
  if (sampleCols !== cols * SAMPLES || footprints.rows !== rows * SAMPLES) throw new Error('Footprint samples are for another grid')

  const coverage = new Float32Array(cols * rows)
  const tallest = new Uint8Array(cols * rows)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      let covered = 0
      let top = 0
      for (let sr = 0; sr < SAMPLES; sr++) {
        for (let sc = 0; sc < SAMPLES; sc++) {
          const value = samples[(row * SAMPLES + sr) * sampleCols + col * SAMPLES + sc]
          if (!value) continue
          covered += 1
          if (value > top) top = value
        }
      }
      coverage[row * cols + col] = covered / (SAMPLES * SAMPLES)
      tallest[row * cols + col] = top
    }
  }

  // Cells with a road across them: WorldCover's built-up there stays open, so an unmapped town keeps its streets.
  const road = new Uint8Array(cols * rows)
  const mark = (x: number, y: number) => {
    const col = Math.floor((x + spec.widthM / 2) / spec.cellM)
    const row = Math.floor((spec.heightM / 2 - y) / spec.cellM)
    if (col >= 0 && col < cols && row >= 0 && row < rows) road[row * cols + col] = 1
  }
  for (const line of roads) {
    for (let index = 2; index < line.length; index += 2) {
      const ax = line[index - 2]
      const ay = line[index - 1]
      const steps = Math.max(1, Math.ceil(Math.hypot(line[index] - ax, line[index + 1] - ay) / (spec.cellM / 2)))
      for (let step = 0; step <= steps; step++) mark(ax + ((line[index] - ax) * step) / steps, ay + ((line[index + 1] - ay) * step) / steps)
    }
  }

  // Mapped buildings within reach of each cell: where there are none, OpenStreetMap hasn't mapped this town.
  const mapped = new Uint8Array(cols * rows)
  for (let index = 0; index < coverage.length; index++) if (coverage[index] > 0) mapped[index] = 1
  const near = new Uint8Array(cols * rows)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (!mapped[row * cols + col]) continue
      for (let dr = -MAPPED_REACH_CELLS; dr <= MAPPED_REACH_CELLS; dr++) {
        for (let dc = -MAPPED_REACH_CELLS; dc <= MAPPED_REACH_CELLS; dc++) {
          const r = row + dr
          const c = col + dc
          if (r >= 0 && r < rows && c >= 0 && c < cols) near[r * cols + c] = 1
        }
      }
    }
  }

  const values = new Uint8Array(cols * rows)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const index = row * cols + col
      if (coverage[index] >= BUILT_COVERAGE) {
        values[index] = heightClass(tallest[index] - 1) || (coverage[index] >= DENSE_COVERAGE ? 2 : 1)
        continue
      }
      if (near[index] || road[index]) continue
      let built = 0
      for (let tr = 0; tr < TYPE_CELLS_PER_BLOCK; tr++) {
        for (let tc = 0; tc < TYPE_CELLS_PER_BLOCK; tc++) {
          const r = Math.min(types.rows - 1, row * TYPE_CELLS_PER_BLOCK + tr)
          const c = Math.min(types.cols - 1, col * TYPE_CELLS_PER_BLOCK + tc)
          if (types.values[r * types.cols + c] === builtCode) built += 1
        }
      }
      if (built * 2 >= TYPE_CELLS_PER_BLOCK * TYPE_CELLS_PER_BLOCK) values[index] = 1
    }
  }
  return { ...spec, values }
}

export function blockCount(grid: Pick<BuildingGrid, 'values'>): number {
  let count = 0
  for (const value of grid.values) if (value) count += 1
  return count
}

class Writer {
  private bytes = new Uint8Array(4096)
  length = 0

  private room(extra: number) {
    if (this.length + extra <= this.bytes.length) return
    const grown = new Uint8Array(Math.max(this.bytes.length * 2, this.length + extra))
    grown.set(this.bytes.subarray(0, this.length))
    this.bytes = grown
  }

  varint(value: number) {
    this.room(5)
    let rest = value >>> 0
    while (rest >= 0x80) {
      this.bytes[this.length++] = (rest & 0x7f) | 0x80
      rest >>>= 7
    }
    this.bytes[this.length++] = rest
  }

  run(values: Uint8Array, from: number, to: number) {
    this.room(to - from)
    this.bytes.set(values.subarray(from, to), this.length)
    this.length += to - from
  }

  result(): Uint8Array {
    return this.bytes.slice(0, this.length)
  }
}

export class BlocksFormatError extends Error {}

/** The grid as pack bytes: for each row, its number of runs, then each run's gap, length and classes. */
export function encodeBlocks(grid: BuildingGrid): Uint8Array {
  const { cols, rows, values } = grid
  const writer = new Writer()
  const runs: [number, number][] = []
  for (let row = 0; row < rows; row++) {
    runs.length = 0
    let col = 0
    while (col < cols) {
      while (col < cols && !values[row * cols + col]) col++
      const start = col
      while (col < cols && values[row * cols + col]) col++
      if (col > start) runs.push([start, col])
    }
    writer.varint(runs.length)
    let end = 0
    for (const [start, stop] of runs) {
      writer.varint(start - end)
      writer.varint(stop - start)
      writer.run(values, row * cols + start, row * cols + stop)
      end = stop
    }
  }
  return writer.result()
}

export function decodeBlocks(bytes: Uint8Array, spec: GridSpec): BuildingGrid {
  const { cols, rows } = spec
  const values = new Uint8Array(cols * rows)
  let offset = 0
  const varint = () => {
    let value = 0
    let shift = 0
    for (;;) {
      if (offset >= bytes.length) throw new BlocksFormatError('World pack blocks run past their section')
      const byte = bytes[offset++]
      value += (byte & 0x7f) * 2 ** shift
      if (byte < 0x80) return value
      shift += 7
      if (shift > 28) throw new BlocksFormatError('World pack blocks hold a number too large')
    }
  }
  for (let row = 0; row < rows; row++) {
    const runCount = varint()
    let col = 0
    for (let run = 0; run < runCount; run++) {
      col += varint()
      const length = varint()
      if (col + length > cols || offset + length > bytes.length) throw new BlocksFormatError('World pack blocks run past their grid')
      values.set(bytes.subarray(offset, offset + length), row * cols + col)
      offset += length
      col += length
    }
  }
  return { ...spec, values }
}
